/**
 * Enhanced Toast Component - Multiple Types & Better Styling
 * Stage 8: Error Handling & User Experience
 */

import React, { useEffect } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  message: string;
  isVisible: boolean;
  onClose: () => void;
  duration?: number;
  type?: ToastType;
  title?: string;
  persistent?: boolean; // Don't auto-close
}

const ToastConfig: Record<ToastType, {
  icon: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  iconColor: string;
}> = {
  success: {
    icon: '✓',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-800',
    iconColor: 'bg-green-500'
  },
  error: {
    icon: '✕',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-800',
    iconColor: 'bg-red-500'
  },
  warning: {
    icon: '⚠',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    textColor: 'text-yellow-800',
    iconColor: 'bg-yellow-500'
  },
  info: {
    icon: 'ℹ',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-800',
    iconColor: 'bg-blue-500'
  }
};

export default function Toast({ 
  message, 
  isVisible, 
  onClose, 
  duration = 3000, 
  type = 'success',
  title,
  persistent = false
}: ToastProps) {
  const config = ToastConfig[type];

  useEffect(() => {
    if (isVisible && !persistent) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose, persistent]);

  if (!isVisible) return null;

  return (
    <div className="fixed top-4 right-4 z-50 animate-fade-in max-w-md">
      <div className={`${config.bgColor} border ${config.borderColor} rounded-lg shadow-lg px-4 py-3 flex items-start gap-3`}>
        <div className={`w-6 h-6 ${config.iconColor} rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-0.5`}>
          {config.icon}
        </div>
        
        <div className="flex-1 min-w-0">
          {title && (
            <h4 className={`font-semibold text-sm mb-1 ${config.textColor}`}>
              {title}
            </h4>
          )}
          <p className={`text-sm ${config.textColor} break-words`}>
            {message}
          </p>
        </div>
        
        <button
          onClick={onClose}
          className={`${config.textColor} hover:opacity-70 transition-opacity flex-shrink-0 text-lg leading-none`}
          aria-label="Close notification"
        >
          ×
        </button>
      </div>
    </div>
  );
}

// Utility functions for common toast patterns
export const showSuccessToast = (message: string, title?: string) => ({
  message,
  title,
  type: 'success' as ToastType,
  duration: 3000
});

export const showErrorToast = (message: string, title?: string, persistent = false) => ({
  message,
  title,
  type: 'error' as ToastType,
  duration: persistent ? undefined : 5000,
  persistent
});

export const showWarningToast = (message: string, title?: string) => ({
  message,
  title,
  type: 'warning' as ToastType,
  duration: 4000
});

export const showInfoToast = (message: string, title?: string) => ({
  message,
  title,
  type: 'info' as ToastType,
  duration: 3000
});
